/**
 * Auth Service — Cognito authentication via AWS Amplify.
 *
 * Provides login, logout, token access, and a shared getAuthHeaders()
 * function that all API modules should use for authenticated requests.
 *
 * Amplify automatically handles:
 *   - Token storage (in-memory + browser storage)
 *   - Silent token refresh via Refresh Token
 *   - Token expiration detection
 */

import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword,
} from 'aws-amplify/auth';

// ---------------------------------------------------------------------------
// Authentication
// ---------------------------------------------------------------------------

/**
 * Sign in directly with Cognito via Amplify.
 * No backend Lambda involved in authentication.
 *
 * @param {string} username
 * @param {string} password
 * @returns {Promise<{success: boolean, challenge?: object, error?: string}>}
 */
export async function login(username, password) {
  try {
    // Clear any stale Amplify auth state from a previous session or
    // incomplete sign-in flow to avoid "already a signed in user" errors
    await signOut();
  } catch {
    // Ignore — signOut throws if nobody is signed in
  }
  try {
    const result = await signIn({ username, password });
    if (result.isSignedIn) {
      return { success: true };
    }
    // Handle challenges (NEW_PASSWORD_REQUIRED, MFA, etc.)
    return { success: false, challenge: result.nextStep };
  } catch (err) {
    return { success: false, error: err.message || 'Authentication failed' };
  }
}

/**
 * Sign out — clears all Amplify-managed tokens.
 */
export async function logout() {
  try {
    await signOut();
  } catch (err) {
    console.error('[auth] Sign out error:', err);
  }
}

// ---------------------------------------------------------------------------
// Password Reset (ForgotPassword flow)
// ---------------------------------------------------------------------------

/**
 * Step 1: ask Cognito to email a reset code to the user.
 * Returns a masked destination (e.g. "t***@p***.com") on success so the
 * UI can confirm where the code went.
 */
export async function requestPasswordReset(username) {
  try {
    const result = await resetPassword({ username });
    const destination = result?.nextStep?.codeDeliveryDetails?.destination || null;
    return { success: true, destination };
  } catch (err) {
    return { success: false, error: mapResetError(err) };
  }
}

/**
 * Step 2: submit the code + new password to finish the reset.
 */
export async function submitPasswordReset(username, code, newPassword) {
  try {
    await confirmResetPassword({ username, confirmationCode: code, newPassword });
    return { success: true };
  } catch (err) {
    return { success: false, error: mapResetError(err) };
  }
}

/**
 * Translate Cognito error names into user-facing copy. We intentionally do
 * NOT distinguish "user not found" from a successful request — leaking that
 * lets attackers enumerate accounts.
 */
function mapResetError(err) {
  switch (err?.name) {
    case 'UserNotFoundException':
    case 'InvalidParameterException':
      return 'If an account exists for that username, a code has been sent.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return 'Too many attempts. Please try again later.';
    case 'CodeMismatchException':
      return 'Incorrect code. Please check your email and try again.';
    case 'ExpiredCodeException':
      return 'That code has expired. Please request a new one.';
    case 'InvalidPasswordException':
      return 'Password must be at least 6 characters.';
    case 'NotAuthorizedException':
      return 'This account cannot reset its password. Contact your administrator.';
    default:
      return err?.message || 'Something went wrong. Please try again.';
  }
}

// ---------------------------------------------------------------------------
// Token Access
// ---------------------------------------------------------------------------

/**
 * Get the current ID token for API requests.
 * Amplify automatically refreshes expired tokens using the Refresh Token.
 *
 * @returns {Promise<string|null>} The ID token string, or null if not authenticated
 */
export async function getIdToken() {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch {
    return null;
  }
}

/**
 * Check if user is currently authenticated with valid tokens.
 *
 * @returns {Promise<boolean>}
 */
export async function isAuthenticated() {
  try {
    await getCurrentUser();
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Shared Auth Headers
// ---------------------------------------------------------------------------

/**
 * Returns standard auth headers for all PalCare API requests.
 * Replaces every `'x-amz-security-token': import.meta.env.VITE_AUTH_TOKEN`
 * with a per-user JWT Bearer token.
 *
 * Usage:
 *   const headers = await getAuthHeaders();
 *   fetch(url, { method: 'POST', headers, body });
 *
 * @returns {Promise<object>} Headers object with Content-Type and Authorization
 */
export async function getAuthHeaders() {
  const idToken = await getIdToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${idToken}`,
  };
}
